// The Promotions tab (spec/13 §18, spec/06 §8.4–§8.5; F-POS-03, F-POS-02, F-POS-06): what the
// organisation takes off a sale or gives away, in three sections under one tab.
//
//   - Automatic promotions  applied by rule when a sale is placed — no code. New / edit is a four-step
//                           Stepper (what it applies to → the discount → when and limits → confirm);
//                           each row pauses or resumes in place (`updatePromotion`, `active`).
//   - Discount codes        the codes staff type at checkout. New is a two-step Stepper; each row can be
//                           deactivated or reactivated in place (`updateDiscountCode`, `active`).
//   - Coupon batches        codes that grant a wallet bonus when redeemed. New is a two-step Stepper;
//                           the codes come back once, in the create response, to copy or download.
//
// Listing promotions and batches needs `discounts:manage`; listing codes `discounts:read`; creating any
// of them `discounts:manage`. A member without them sees the sections they can read, and no action.
// The server checks every permission again (spec/13 §1).

import type { CouponBatch, DiscountCode, Promotion } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, EmptyState, Table, useToast, type TableColumn } from '@clavian/ui-web';
import { useMemo, useState, type ReactNode } from 'react';

import { useAccess, useMyVenues } from '../../api/queries';
import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { Icon, NavIcon } from '../../shell/nav-icons';
import { LoadFailure } from '../../shell/LoadFailure';
import { useScopeIds } from '../../shell/scope';
import { Forbidden, GuardedDialog, Notice, Section, TableSkeleton } from '../../ui';
import { CouponBatchFlow } from './CouponBatchFlow';
import { DiscountCodeFlow } from './DiscountCodeFlow';
import { PromotionFlow } from './PromotionFlow';
import { useCouponBatches, useDiscountCodes, usePromotions, useSetDiscountCodeActive, useUpdatePromotion } from './queries';
import { consoleConditionWords, consoleOfferWords, offerStatus, STATUS_RAIL, STATUS_TONE, timeOfMinute, valueWords } from './terms';

const DISCOUNTS_READ = 'discounts:read';
const DISCOUNTS_MANAGE = 'discounts:manage';

export function PromotionsTab(): ReactNode {
  const { t } = useTranslation('console');
  const ids = useScopeIds();
  const access = useAccess(ids.access);
  const ready = access.status === 'ready';
  const canManage = ready && access.can(DISCOUNTS_MANAGE);
  const canRead = ready && (access.can(DISCOUNTS_READ) || canManage);

  const [creating, setCreating] = useState<'promotion' | 'code' | 'batch' | null>(null);
  const [editing, setEditing] = useState<Promotion | null>(null);

  if (ready && !canRead) {
    return (
      <div className="console-pricing-panel">
        <Forbidden message={t('pricing.promotions.notPermitted')} />
      </div>
    );
  }

  return (
    <div className="console-pricing-panel">
      {ready && !canManage ? <Notice role="note">{t('pricing.promotions.readOnly')}</Notice> : null}

      <Section
        title={t('pricing.promotions.title')}
        tip={t('pricing.promotions.intro')}
        actions={
          canManage ? (
            <Button variant="primary" onClick={() => setCreating('promotion')}>
              {t('pricing.promotions.new')}
            </Button>
          ) : undefined
        }
      >
        {canManage ? <PromotionList organizationId={ids.organizationId} onEdit={setEditing} onCreate={() => setCreating('promotion')} /> : <p className="console-subtle">{t('pricing.promotions.needsManage')}</p>}
      </Section>

      <Section
        title={t('pricing.codes.title')}
        tip={t('pricing.codes.intro')}
        body="rows"
        actions={
          canManage ? (
            <Button variant="secondary" onClick={() => setCreating('code')}>
              {t('pricing.codes.new')}
            </Button>
          ) : undefined
        }
      >
        {canRead ? <DiscountCodeTable organizationId={ids.organizationId} enabled={ready} canManage={canManage} /> : <TableSkeleton rows={3} />}
      </Section>

      <Section
        title={t('pricing.coupons.title')}
        tip={t('pricing.coupons.intro')}
        body="rows"
        actions={
          canManage ? (
            <Button variant="secondary" onClick={() => setCreating('batch')}>
              {t('pricing.coupons.new')}
            </Button>
          ) : undefined
        }
      >
        {canManage ? <CouponBatchTable organizationId={ids.organizationId} /> : <p className="console-subtle console-pricing-inset">{t('pricing.coupons.needsManage')}</p>}
      </Section>

      <Dialog open={creating === 'promotion'} onOpenChange={(open) => setCreating(open ? 'promotion' : null)} size="lg" dirty="auto" title={t('pricing.promotionFlow.title')} description={t('pricing.promotionFlow.hint')}>
        {creating === 'promotion' ? <PromotionFlow onDone={() => setCreating(null)} /> : null}
      </Dialog>
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => (open ? undefined : setEditing(null))}
        size="lg"
        dirty="auto"
        eyebrow={editing?.name}
        title={t('pricing.promotionFlow.editTitle')}
        description={t('pricing.promotionFlow.editHint')}
      >
        {editing !== null ? <PromotionFlow editing={editing} onDone={() => setEditing(null)} /> : null}
      </Dialog>
      <Dialog open={creating === 'code'} onOpenChange={(open) => setCreating(open ? 'code' : null)} size="md" dirty="auto" title={t('pricing.codeFlow.title')} description={t('pricing.codeFlow.hint')}>
        {creating === 'code' ? <DiscountCodeFlow onDone={() => setCreating(null)} /> : null}
      </Dialog>
      <GuardedDialog
        open={creating === 'batch'}
        onClose={() => setCreating(null)}
        size="md"
        title={t('pricing.couponFlow.title')}
        description={t('pricing.couponFlow.hint')}
        guard={{
          title: t('pricing.couponFlow.guardTitle'),
          body: t('pricing.couponFlow.guardBody'),
          keep: t('common.guard.keep'),
          discard: t('common.guard.discard'),
        }}
      >
        {(setGuarded) => (creating === 'batch' ? <CouponBatchFlow onDone={() => setCreating(null)} onGuardChange={setGuarded} /> : null)}
      </GuardedDialog>
    </div>
  );
}

// -------------------------------------------------------------------------------------------------
// Automatic promotions
// -------------------------------------------------------------------------------------------------

function PromotionList({ organizationId, onEdit, onCreate }: { readonly organizationId: string; readonly onEdit: (promotion: Promotion) => void; readonly onCreate: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const promotions = usePromotions(organizationId);

  if (promotions.data === undefined) {
    if (promotions.isError && problemCode(promotions.error) === PROBLEM.forbidden) return <p className="console-subtle">{t('pricing.promotions.needsManage')}</p>;
    return promotions.isError ? <LoadFailure error={promotions.error} message={t('pricing.promotions.unavailable')} onRetry={() => void promotions.refetch()} /> : <TableSkeleton rows={2} />;
  }
  if (promotions.data.length === 0) {
    return <EmptyState size="compact" icon={<NavIcon name="promotions" />} title={t('pricing.promotions.emptyTitle')} description={t('pricing.promotions.empty')} action={<Button variant="primary" onClick={onCreate}>{t('pricing.promotions.new')}</Button>} />;
  }
  return (
    <ul className="console-promo-list" aria-label={t('pricing.promotions.listLabel')}>
      {promotions.data.map((promotion) => (
        <li key={promotion.id}>
          <PromotionRow organizationId={organizationId} promotion={promotion} onEdit={() => onEdit(promotion)} />
        </li>
      ))}
    </ul>
  );
}

function PromotionRow({ organizationId, promotion, onEdit }: { readonly organizationId: string; readonly promotion: Promotion; readonly onEdit: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const { show } = useToast();
  const problem = useProblem();
  const update = useUpdatePromotion(organizationId);
  const [now] = useState(() => Date.now());
  const status = offerStatus(promotion, now);
  const rules = useRuleWords(promotion);

  function toggle(): void {
    const next = !promotion.active;
    update.mutate(
      { promotionId: promotion.id, body: { active: next } },
      {
        onSuccess: () => show({ tone: 'ok', title: next ? t('pricing.promotions.resumed', { name: promotion.name }) : t('pricing.promotions.paused', { name: promotion.name }) }),
        onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('pricing.promotions.toggleFailed') }) }),
      },
    );
  }

  return (
    <article className={`console-promo-row console-rail console-rail--start${promotion.active ? '' : ' is-paused'}`} data-rail={STATUS_RAIL[status]}>
      <div className="console-promo-row__main">
        <div className="console-promo-row__head">
          <h3 className="console-promo-row__name">{promotion.name}</h3>
          <Badge tone={STATUS_TONE[status]} dot>
            {t(`pricing.status.${status}`)}
          </Badge>
          {promotion.stackable ? <Badge tone="neutral">{t('pricing.promotions.stackable')}</Badge> : null}
        </div>
        <p className="console-promo-row__value">
          {promotion.type === 'console'
            ? promotion.console === undefined
              ? '—'
              : consoleOfferWords(t, format.money, promotion.console, promotion.maxDiscountAmount)
            : valueWords(t, format.money, promotion.type, promotion.percentBps, promotion.amount, promotion.maxDiscountAmount, (promotion.rules.appliesToKinds ?? []).includes('play'))}
        </p>
        <ul className="console-promo-rules" aria-label={t('pricing.promotions.rulesLabel', { name: promotion.name })}>
          {rules.map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ul>
      </div>
      <div className="console-promo-row__actions">
        <Button variant="ghost" size="sm" onClick={onEdit} label={t('pricing.promotions.editNamed', { name: promotion.name })}>
          {t('pricing.promotions.edit')}
        </Button>
        <Button variant="secondary" size="sm" busy={update.isPending} onClick={toggle} label={promotion.active ? t('pricing.promotions.pauseNamed', { name: promotion.name }) : t('pricing.promotions.resumeNamed', { name: promotion.name })}>
          {promotion.active ? t('pricing.promotions.pause') : t('pricing.promotions.resume')}
        </Button>
      </div>
    </article>
  );
}

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;

/** A promotion's rules as short phrases: what, where, when, and the limits. */
function useRuleWords(promotion: Promotion): string[] {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  return useMemo(() => {
    const rules = promotion.rules;
    const words: string[] = [];
    const kinds = rules.appliesToKinds ?? [];
    // A console offer (ADR-0034) names console sessions and its own conditions instead of line kinds.
    if (promotion.type === 'console' && promotion.console !== undefined) words.push(...consoleConditionWords(t, promotion.console));
    else words.push(kinds.length === 0 ? t('pricing.rules.everything') : kinds.map((kind) => t(`pricing.kind.${kind}`)).join(', '));
    if ((rules.productIds ?? []).length > 0) words.push(t('pricing.rules.products', { count: rules.productIds?.length ?? 0 }));
    const days = rules.daysOfWeek ?? [];
    if (days.length > 0 && days.length < 7) words.push([...days].sort().map((day) => t(`passes.day.${DAY_KEYS[day] ?? 'mon'}`)).join(' '));
    if (rules.startMinuteOfDay != null && rules.endMinuteOfDay != null) words.push(t('pricing.rules.window', { from: timeOfMinute(rules.startMinuteOfDay), to: timeOfMinute(rules.endMinuteOfDay) }));
    const channels = rules.channels ?? [];
    if (channels.length > 0) words.push(channels.map((channel) => t(`pricing.channel.${channel}`)).join(', '));
    const venues = rules.venueIds ?? [];
    words.push(venues.length === 0 ? t('pricing.rules.allVenues') : t('pricing.rules.venues', { count: venues.length }));
    if (rules.minOrderAmount != null) words.push(t('pricing.rules.minOrder', { amount: format.money(rules.minOrderAmount) }));
    if (rules.firstOrderOnly === true) words.push(t('pricing.rules.firstOrder'));
    if (promotion.validUntil !== null) words.push(t('pricing.rules.until', { date: format.date(promotion.validUntil) }));
    return words;
  }, [promotion, t, format]);
}

// -------------------------------------------------------------------------------------------------
// Discount codes
// -------------------------------------------------------------------------------------------------

function DiscountCodeTable({ organizationId, enabled, canManage }: { readonly organizationId: string; readonly enabled: boolean; readonly canManage: boolean }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const codes = useDiscountCodes(organizationId, enabled);
  const [now] = useState(() => Date.now());

  const columns = useMemo<readonly TableColumn<DiscountCode>[]>(
    () => [
      { key: 'code', header: t('pricing.codes.column.code'), cell: (code) => <span className="console-promo-code">{code.code}</span> },
      { key: 'value', header: t('pricing.codes.column.value'), cell: (code) => valueWords(t, format.money, code.type, code.percentBps, code.amount, code.maxDiscount) },
      { key: 'minOrder', header: t('pricing.codes.column.minOrder'), numeric: true, cell: (code) => (code.minOrder === null ? '—' : format.money(code.minOrder)) },
      {
        key: 'used',
        header: t('pricing.codes.column.used'),
        numeric: true,
        cell: (code) => (code.usageLimit === null ? String(code.usageCount) : t('pricing.codes.usedOf', { used: code.usageCount, limit: code.usageLimit })),
      },
      { key: 'until', header: t('pricing.codes.column.until'), cell: (code) => (code.validUntil === null ? t('pricing.noEnd') : format.date(code.validUntil)) },
      {
        key: 'status',
        header: t('pricing.codes.column.status'),
        cell: (code) => {
          if (!code.active) {
            return (
              <Badge tone="neutral" dot>
                {t('pricing.codes.inactive')}
              </Badge>
            );
          }
          const status = offerStatus(code, now, code.usageLimit !== null && code.usageCount >= code.usageLimit);
          return (
            <Badge tone={STATUS_TONE[status]} dot>
              {t(`pricing.status.${status}`)}
            </Badge>
          );
        },
      },
      ...(canManage
        ? [{ key: 'actions', header: t('pricing.codes.column.actions'), cell: (code: DiscountCode) => <DiscountCodeToggle organizationId={organizationId} code={code} /> }]
        : []),
    ],
    [t, format, now, canManage, organizationId],
  );

  if (codes.data === undefined) {
    return codes.isError ? <LoadFailure error={codes.error} message={t('pricing.codes.unavailable')} onRetry={() => void codes.refetch()} /> : <TableSkeleton rows={3} />;
  }
  return (
    <Table
      caption={t('pricing.codes.caption')}
      captionHidden
      columns={columns}
      rows={codes.data}
      rowKey={(code) => code.id}
      rowTone={(code) => STATUS_RAIL[offerStatus(code, now, code.usageLimit !== null && code.usageCount >= code.usageLimit)]}
      empty={<EmptyState size="compact" icon={<Icon name="tag" />} title={t('pricing.codes.empty')} />}
    />
  );
}

/** Deactivate (or reactivate) one code in place. A deactivated code is refused at checkout. */
function DiscountCodeToggle({ organizationId, code }: { readonly organizationId: string; readonly code: DiscountCode }): ReactNode {
  const { t } = useTranslation('console');
  const { show } = useToast();
  const problem = useProblem();
  const toggle = useSetDiscountCodeActive(organizationId);

  function flip(): void {
    const next = !code.active;
    toggle.mutate(
      { discountCodeId: code.id, active: next },
      {
        onSuccess: () => show({ tone: 'ok', title: next ? t('pricing.codes.reactivated', { code: code.code }) : t('pricing.codes.deactivated', { code: code.code }) }),
        onError: (failure) => show({ tone: 'danger', title: problem.text(failure, { fallback: t('pricing.codes.toggleFailed') }) }),
      },
    );
  }

  return (
    <Button
      variant={code.active ? 'ghost' : 'secondary'}
      size="sm"
      busy={toggle.isPending}
      onClick={flip}
      label={code.active ? t('pricing.codes.deactivateNamed', { code: code.code }) : t('pricing.codes.reactivateNamed', { code: code.code })}
    >
      {code.active ? t('pricing.codes.deactivate') : t('pricing.codes.reactivate')}
    </Button>
  );
}

// -------------------------------------------------------------------------------------------------
// Coupon batches
// -------------------------------------------------------------------------------------------------

function CouponBatchTable({ organizationId }: { readonly organizationId: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const batches = useCouponBatches(organizationId);
  const myVenues = useMyVenues();
  const [now] = useState(() => Date.now());
  // Where a batch redeems (#709): every venue, or the venue(s) it names, by name where this member can see them.
  const venueNames = useMemo(() => new Map((myVenues.data ?? []).map((venue) => [venue.id, venue.name])), [myVenues.data]);

  const columns = useMemo<readonly TableColumn<CouponBatch>[]>(
    () => [
      { key: 'name', header: t('pricing.coupons.column.name'), cell: (batch) => <span className="console-promo-batch">{batch.batchName}</span> },
      { key: 'grant', header: t('pricing.coupons.column.grant'), cell: (batch) => (batch.grantAmount === null ? '—' : t('pricing.coupons.walletBonus', { amount: format.money(batch.grantAmount) })) },
      { key: 'codes', header: t('pricing.coupons.column.codes'), numeric: true, cell: (batch) => String(batch.issuedCount) },
      { key: 'redeemed', header: t('pricing.coupons.column.redeemed'), numeric: true, cell: (batch) => String(batch.redeemedCount) },
      { key: 'uses', header: t('pricing.coupons.column.uses'), cell: (batch) => (batch.maxRedemptionsPerCode === 1 ? t('pricing.coupons.singleUse') : t('pricing.coupons.multiUse', { count: batch.maxRedemptionsPerCode })) },
      { key: 'until', header: t('pricing.coupons.column.until'), cell: (batch) => (batch.validUntil === null ? t('pricing.noEnd') : format.date(batch.validUntil)) },
      {
        key: 'where',
        header: t('pricing.coupons.column.where'),
        cell: (batch) =>
          batch.scope === 'organization'
            ? t('pricing.coupons.whereAll')
            : t('pricing.coupons.whereVenues', {
                venues: batch.venueIds.length === 1 ? (venueNames.get(batch.venueIds[0] ?? '') ?? t('pricing.scopeChoice.severalVenues', { count: 1 })) : t('pricing.scopeChoice.severalVenues', { count: batch.venueIds.length }),
              }),
      },
      {
        key: 'status',
        header: t('pricing.coupons.column.status'),
        cell: (batch) => {
          const status = offerStatus(batch, now);
          return (
            <Badge tone={STATUS_TONE[status]} dot>
              {t(`pricing.status.${status}`)}
            </Badge>
          );
        },
      },
    ],
    [t, format, now, venueNames],
  );

  if (batches.data === undefined) {
    if (batches.isError && problemCode(batches.error) === PROBLEM.forbidden) return <p className="console-subtle console-pricing-inset">{t('pricing.coupons.needsManage')}</p>;
    return batches.isError ? <LoadFailure error={batches.error} message={t('pricing.coupons.unavailable')} onRetry={() => void batches.refetch()} /> : <TableSkeleton rows={2} />;
  }
  return (
    <Table
      caption={t('pricing.coupons.caption')}
      captionHidden
      columns={columns}
      rows={batches.data}
      rowKey={(batch) => batch.id}
      rowTone={(batch) => STATUS_RAIL[offerStatus(batch, now)]}
      empty={<EmptyState size="compact" icon={<Icon name="gift" />} title={t('pricing.coupons.empty')} />}
    />
  );
}
